import type { ComponentChildren } from 'preact';
import { Redirect, useLocation } from 'wouter-preact';
import { useQuery } from '@/hooks/useQuery';
import { companiesApi, integrationsApi, syncApi, usersApi } from '@/lib/api/endpoints';
import { countConnectedIntegrations, isMandatoryComplete } from '@/lib/onboarding';

const ONBOARDING_PATH = '/configuracion/inicio';

/**
 * Gate global de onboarding. Mientras falte la configuración estrictamente
 * necesaria (empresa + integración conectada + sync con éxito), redirige
 * CUALQUIER ruta a `/configuracion/inicio`. Se permite navegar dentro de
 * `/configuracion/*` para que el usuario pueda completarla.
 *
 * A diferencia del antiguo gate de Inicio, esto NO respeta "saltar": lo
 * obligatorio no es saltable. El skip solo aplica a pasos opcionales (equipo).
 *
 * Se monta dentro del AppShell, así que el usuario conserva sidebar/topbar
 * para moverse por Configuración mientras lo resuelve.
 */
export function OnboardingGate({ children }: { children: ComponentChildren }) {
  const [path] = useLocation();

  // Mismas keys que OnboardingPage/Configuración → cache compartida.
  const users = useQuery('configuracion.users.count', async () => {
    const { data, error } = await usersApi.list();
    if (error) throw new Error('No se pudo cargar usuarios');
    return Array.isArray(data) ? data.length : 0;
  });
  const companies = useQuery('configuracion.companies.count', async () => {
    const { data, error } = await companiesApi.list();
    if (error) throw new Error('No se pudo cargar empresas');
    return Array.isArray(data) ? data.length : 0;
  });
  const overview = useQuery('configuracion.integrations.overview', async () => {
    const { data, error } = await integrationsApi.overview();
    if (error) throw new Error('No se pudo cargar integraciones');
    return data;
  });
  // Señal canónica de "ha sincronizado": el backend la deriva de `sync_state`
  // (lo que mantiene el orquestador), no de runs por proveedor. Tolerante a
  // error → "sin sync".
  const readiness = useQuery('configuracion.sync.readiness', async () => {
    const { data, error } = await syncApi.readiness();
    if (error) return null;
    return data;
  });

  const loading =
    users.loading.value ||
    companies.loading.value ||
    overview.loading.value ||
    readiness.loading.value;
  const error = users.error.value || companies.error.value || overview.error.value;

  // Permitimos siempre Configuración: es donde se arregla el setup. Sin esto el
  // gate redirigiría /configuracion/integraciones → /configuracion/inicio en
  // bucle y el usuario no podría avanzar.
  const onConfig = path.startsWith('/configuracion');

  // Solo redirigir cuando los datos del gate REALMENTE se han cargado. `loading`
  // arranca en `false` (el fetch de useQuery ocurre en un effect tras el primer
  // render), así que mirar solo `!loading` provocaba un redirect a onboarding en
  // el primer paint —con datos aún `undefined`— incluso para tenants ya
  // configurados (quedaban atrapados en /configuracion/inicio). Exigimos que las
  // queries que decide `isMandatoryComplete` tengan dato antes de decidir.
  const dataReady =
    companies.data.value !== undefined &&
    overview.data.value !== undefined &&
    readiness.data.value !== undefined;

  const reviewReadiness = readiness.data.value;
  const isLocalDevelopment =
    import.meta.env.DEV && ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const isLocalReviewSurface =
    path === '/' ||
    path === '/pedidos' ||
    path === '/control' ||
    path === '/reporting/inteligencia' ||
    path === '/reporting/semanal' ||
    path === '/reporting/validacion-asistente' ||
    path.startsWith('/creativos') ||
    path.startsWith('/web') ||
    path.startsWith('/clientes') ||
    path === '/dashboard-v2/ventas' ||
    path === '/dashboard-v2/clientes-ltv' ||
    path === '/dashboard-v2/klaviyo' ||
    path === '/dashboard-v2/meta-ads' ||
    path === '/dashboard-v2/landings-funnel' ||
    path === '/dashboard-v2/clarity' ||
    path === '/dashboard-v2/productos-ofertas';

  if (
    isLocalReviewSurface &&
    (isLocalDevelopment ||
      (reviewReadiness &&
        'review_snapshot_available' in reviewReadiness &&
        reviewReadiness.review_snapshot_available === true))
  ) {
    return <>{children}</>;
  }

  if (dataReady && !loading && !error && !onConfig) {
    const hasSuccessfulSync = readiness.data.value?.has_successful_sync === true;
    const mandatory = isMandatoryComplete({
      companies: companies.data.value ?? 0,
      users: users.data.value ?? 0,
      connectedIntegrations: countConnectedIntegrations(overview.data.value),
      hasSuccessfulSync,
    });
    if (!mandatory) {
      return <Redirect to={ONBOARDING_PATH} />;
    }
  }

  return <>{children}</>;
}
